/**
 * iLand Voice Bot - Type Definitions
 */

// Voice options available from Azure OpenAI
export type VoiceOption = 'alloy' | 'echo' | 'shimmer' | 'ash' | 'ballad' | 'coral' | 'sage' | 'verse' | 'marin' | 'cedar';

// Language options - ISO 639-1 codes for explicit language selection
// 'auto' is deprecated - we now require explicit language selection via picker
export type LanguageOption = 'he' | 'en' | 'ru' | 'fr' | 'ar';

// Multi-channel assistant — WIDGET-FACING channels only.
// Note: 'whatsapp' is intentionally EXCLUDED — WhatsApp is server-only and
// must never reach the public widget payload (see services/prompt-builder.js
// allowlist). This guarantees the chooser can never offer WhatsApp.
export const WIDGET_CHANNELS = ['voice', 'chat'] as const;
export type WidgetChannel = typeof WIDGET_CHANNELS[number];

// Per-customer widget-facing channel settings (allowlisted subset of
// channel_settings.widget from the dashboard). The server prompt-builder
// is the source of truth for which fields are exposed.
export interface ChannelSettingsWidget {
  defaultChannel?: WidgetChannel;
  rememberLastUsedChannel?: boolean;
}
export interface ChannelSettings {
  widget?: ChannelSettingsWidget;
}

// Message in transcript
export interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  links?: Array<{ label: string; url: string }>; // Affiliate links injected at render time (never sent to model)
}

// Calendly slot
export interface CalendlySlot {
  start: string;
  end: string;
  formatted: string;
  dayOfWeek: string;
  hour: number;
}

// Session data from voice-session API
export interface VoiceSessionData {
  success: boolean;
  sessionId: string;
  ephemeralKey: string;
  deployment: string;
  endpoint: string;
  apiVersion: string; // Azure OpenAI API version for WebRTC connection
  region: string;
  tools: FunctionTool[];
  customerId?: string; // Customer ID for multi-tenant usage tracking
  industry?: string; // Customer industry (e.g., real_estate, insurance)
  propertyName?: string; // Property name for context-aware greeting (e.g., "מתחם 24, חדרה")
  voiceHardening?: VoiceHardeningConfig;
  widgetConfig?: Partial<WidgetConfig>; // Customer-specific widget config from DB (overrides defaults)
}

export interface VoiceHardeningSettings {
  echo_cancellation: boolean;
  noise_suppression: boolean;
  auto_gain_control: boolean;
  vad_threshold: number;
  vad_silence_ms: number;
  vad_prefix_padding_ms: number;
  interrupt_response: boolean;
}

export interface VoiceHardeningConfig {
  enabled: boolean;
  source?: 'global_disabled' | 'global_defaults' | 'customer_override' | 'customer_override_disabled' | 'error_fallback';
  settings: VoiceHardeningSettings;
}

// Function tool definition
export interface FunctionTool {
  type: 'function';
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, {
      type: string;
      description: string;
      enum?: string[];
    }>;
    required: string[];
  };
}

// Knowledge base result
export interface KnowledgeResult {
  id: string;
  title: string;
  content: string;
  category: string;
  similarity?: number;
}

// Contact information collected from user
export interface ContactInfo {
  name?: string;
  email: string;
  phone: string;
}

// Scheduled meeting details
export interface ScheduledMeeting {
  datetime: string;
  formattedTime: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  bookingUrl?: string;
  source?: 'calendly' | 'verbal' | 'callback';
  notes?: string;
}

// Conversation outcome
export type ConversationOutcome = 
  | 'scheduled'      // Meeting was scheduled
  | 'interested'     // Showed interest but didn't schedule
  | 'question_answered'  // Questions answered, no scheduling
  | 'no_interest'    // Not interested
  | 'abandoned'      // Left without completion
  | 'unknown';

// Widget configuration - flat structure for component props
export interface WidgetConfig {
  onInteraction?: (event: 'avatar' | 'voice' | 'chat') => void | Promise<void>;
  publicKey?: string; // Customer public key for multi-tenant authentication (pk_live_xxx or pk_test_xxx)
  botName: string;
  companyName: string;
  greeting: string;
  voice: VoiceOption;
  language: LanguageOption;
  temperature: number;
  primaryColor: string;
  buttonPosition: { bottom: number; right?: number; left?: number };
  panelWidth: number;
  systemPrompt: string;
  callerAddressMode?: 'auto' | 'plural' | 'adaptive_plural' | null;
  calendlyOwnerName: string;
  iconUrl?: string; // Optional custom icon URL for the floating button
  // Dashboard-configurable launcher placement. Overrides buttonPosition when set.
  // Shape: { preset, top?, bottom?, left?, right? } where preset is one of
  // 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'custom'.
  widgetPosition?: WidgetPosition | null;
  // Public init() shorthand for developers to override the dashboard position
  // per page. Accepts a preset string OR a raw offsets object. Normalized
  // internally into widgetPosition. Highest precedence (init > dashboard).
  position?: WidgetPositionPreset | WidgetPosition | { top?: number | string; bottom?: number | string; left?: number | string; right?: number | string };
  // Master on/off switch for the embedded widget. When false, nothing renders.
  widgetEnabled?: boolean;
  // Customer-customizable text shown in the bubble above the floating launcher.
  // undefined → default text ("לחצו עלי ונדבר..."); '' → bubble hidden; non-empty string → custom text.
  widgetBubbleText?: string | null;
  // Customer-customizable text shown UNDER the launcher icon.
  // undefined / null → default text ("לחצו עליי ונשוחח"); '' → CTA hidden; non-empty string → custom text.
  widgetCtaText?: string | null;
  propertyCode?: string; // Property code matching knowledge_base ID (e.g., "HAD4", "KIR1")
  forceVoiceOverride?: boolean; // Demo-only: explicitly override DB voice when true
  // Contact form timing: 'none' (no form), 'before_conversation' (form at start) or 'before_scheduling' (deferred to pre-Calendly)
  contactFormTiming?: 'none' | 'before_conversation' | 'before_scheduling';
  // Google Drive folder IDs for per-customer shortcuts
  recordingsFolderId?: string;
  transcriptsFolderId?: string;
  voiceHardening?: VoiceHardeningConfig;
  // Default panel state: true = block interruptions while the agent speaks.
  blockInterruptionDefault?: boolean;
  // Browser-voice projection of the shared spoken-channel disclosure toggle.
  // undefined / true = browser voice and telephone speak the disclosure;
  // false = both start with the greeting. Text chat remains unaffected.
  voiceRecordingDisclosureEnabled?: boolean;
  // Multi-channel assistant — which channels this bot can answer on in the widget.
  // Server already filters to WIDGET_CHANNELS (no 'whatsapp'). Defaults to ['voice'].
  enabledChannels?: WidgetChannel[];
  // Per-customer widget channel settings (defaultChannel, rememberLastUsedChannel).
  channelSettings?: ChannelSettings;
  // Facebook/Meta Pixel — per-customer browser conversion tracking (voice + chat).
  // The Pixel ID is public by design. When fbPixelEnabled is false / unset, no
  // pixel is injected and behavior is byte-identical to before this feature.
  fbPixelEnabled?: boolean;
  fbPixelId?: string | null;
  // When false, the floating launcher is fixed in place and cannot be dragged.
  // undefined / true → draggable (default, unchanged behavior for all customers).
  draggable?: boolean;
  // Opt-in inline-launcher mode (used only by the Callega demo landing page).
  // When true AND the viewport is mobile-width, the launcher is rendered in the
  // normal document flow inside `inlineMountSelector` (a placeholder element the
  // host page provides) instead of as a fixed floating button — so it scrolls
  // with the page and never overlaps the hero text. Desktop keeps the fixed
  // floating launcher. Other customers never pass this flag, so their behavior
  // is byte-identical.
  inline?: boolean;
  // When true, inline mode applies at every viewport width rather than mobile
  // only. This is opt-in for host pages (currently the Callega /agents landing)
  // that provide a responsive layout anchor. Existing customer embeds never
  // pass it and retain dashboard-controlled fixed positioning.
  inlineAllViewports?: boolean;
  // CSS selector for the host-page element the inline launcher portals into.
  // Defaults to '#ilands-inline-widget-slot' when omitted.
  inlineMountSelector?: string;
  // Opt-in Callega demo launcher effects (used only by the /agents landing
  // page, both breakpoints). When true the rising 0/1 particles emanate from
  // the avatar's circle border outward instead of across the face; combined
  // with `inline` (mobile) it also removes the hard ripple rings and softens
  // the glow into a gentle halo. Other customers never pass this flag.
  landingFx?: boolean;
  // Phase B: per-customer live-transcript engine for the WebRTC voice channel.
  // null/undefined → Realtime built-in transcription (default). 'azure_speech'
  // → widget streams the displayed user transcript from the Azure Speech SDK
  // and disables the Realtime input transcription. Voice-only.
  liveTranscriptEngine?: 'azure_speech' | null;
  // Per-customer Realtime input-transcription model (voice-only). null →
  // default 'gpt-realtime-whisper'. Validated against an allowlist widget-side
  // (see send-initial-greeting.ts) so an unknown value can't break a session.
  realtimeTranscribeModel?: string | null;
}

export type WidgetPositionPreset =
  | 'bottom-right'
  | 'bottom-left'
  | 'top-right'
  | 'top-left'
  | 'center'
  | 'bottom-center'
  | 'custom';

export interface WidgetPosition {
  preset: WidgetPositionPreset;
  // Offsets accept a number (interpreted as px) OR a CSS string (e.g. '20%', '10vh').
  // Percentages are recommended for portable, viewport-relative placement that
  // matches the dashboard preview 1:1 across any visitor screen size.
  top?: number | string;
  bottom?: number | string;
  left?: number | string;
  right?: number | string;
}

// Session state
export interface SessionState {
  isActive: boolean;
  isConnecting: boolean;
  isSpeaking: boolean;
  isMuted: boolean;
  error: string | null;
}

// Transcript payload for n8n
export interface TranscriptPayload {
  event: 'conversation_ended';
  timestamp: string;
  sessionId: string;
  duration: number;
  transcript: Message[];
  outcome: ConversationOutcome;
  scheduledMeeting: ScheduledMeeting | null;
  sentiment: {
    overall: 'positive' | 'neutral' | 'negative';
    score: number;
    highlights: string[];
  } | null;
  source: string;
}
